import { useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { BookOpen, Search, Mail, FileText, BarChart, Sparkles, Zap } from "lucide-react";

const ContentView = () => {
  const { userId } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const contentItems = [
    {
      id: 1,
      type: "Email Template",
      title: "Initial Outreach - Executive",
      description: "Cold outreach template for C-level executives",
      tags: ["cold-outreach", "executive", "first-touch"],
      views: 124,
      sends: 45,
      engagement: 32,
      icon: Mail
    },
    {
      id: 2,
      type: "Email Template",
      title: "Follow-up - Discovery Call",
      description: "Follow-up after initial discovery conversation",
      tags: ["follow-up", "discovery"],
      views: 89,
      sends: 67,
      engagement: 58,
      icon: Mail
    },
    {
      id: 3,
      type: "Presentation",
      title: "Product Demo Deck",
      description: "Comprehensive product demonstration slides",
      tags: ["demo", "product", "visual"],
      views: 234,
      sends: 12,
      engagement: 78,
      icon: FileText
    },
    {
      id: 4,
      type: "Case Study",
      title: "Enterprise Client Success Story",
      description: "ROI case study for Fortune 500 implementation",
      tags: ["case-study", "enterprise", "roi"],
      views: 167,
      sends: 34,
      engagement: 82,
      icon: BarChart
    },
    {
      id: 5,
      type: "Battle Card",
      title: "Competitor Comparison - Salesforce",
      description: "Key differentiators vs Salesforce",
      tags: ["competitive", "salesforce"],
      views: 156,
      sends: 0,
      engagement: 0,
      icon: FileText
    },
    {
      id: 6,
      type: "Email Template",
      title: "Proposal Follow-up",
      description: "Template for following up after proposal delivery",
      tags: ["follow-up", "proposal", "closing"],
      views: 98,
      sends: 52,
      engagement: 44,
      icon: Mail
    }
  ];

  const categories = ["all", "Email Template", "Presentation", "Case Study", "Battle Card"];

  const filteredContent = contentItems.filter(item => {
    const matchesSearch = searchQuery === "" || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === "all" || item.type === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handlePersonalize = (item) => {
    toast.success(`Personalizing "${item.title}" for your selected account...`);
  };

  const handleUse = (item) => {
    toast.success(`Opening "${item.title}"`);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6" 
      data-testid="content-view"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-white mb-2 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-neon-cyan" />
            <span className="text-neon-cyan">Content Library</span>
          </h1>
          <p className="text-sm text-gray-500">Sales content and templates for every stage</p>
        </div>
        <Button className="bg-gradient-to-r from-pink-600/80 to-pink-500/80 hover:from-pink-500/80 hover:to-pink-400/80 text-white">
          <Sparkles className="h-4 w-4 mr-2" />
          Generate New Content
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search content..."
            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            data-testid="search-content"
          />
        </div>
        <div className="flex gap-2">
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? "default" : "outline"}
              size="sm"
              className={selectedCategory === category 
                ? "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30"
                : "border-white/20 text-gray-400 hover:bg-white/5 hover:text-white"
              }
              data-testid={`category-${category.replace(' ', '-').toLowerCase()}`}
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredContent.map((item, idx) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className="glass-card rounded-xl p-5 hover:border-cyan-500/30 transition-all" data-testid={`content-item-${idx}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="p-2.5 bg-cyan-500/10 rounded-lg">
                    <Icon className="h-5 w-5 text-cyan-400/70" />
                  </div>
                  <Badge className="bg-white/5 text-gray-400 border-white/10 text-xs">
                    {item.type}
                  </Badge>
                </div>

                <h3 className="text-base text-white mb-2">{item.title}</h3>
                <p className="text-xs text-gray-500 mb-4">{item.description}</p>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {item.tags.map((tag) => (
                    <Badge key={tag} className="bg-cyan-500/10 text-cyan-400/70 border-cyan-500/20 text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b border-white/10">
                  <div>
                    <p className="text-xs text-gray-600">Views</p>
                    <p className="text-sm text-white">{item.views}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Sends</p>
                    <p className="text-sm text-white">{item.sends}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Engage</p>
                    <p className="text-sm text-cyan-400/80">{item.engagement}%</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleUse(item)}
                    size="sm"
                    className="flex-1 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30"
                  >
                    Use
                  </Button>
                  <Button
                    onClick={() => handlePersonalize(item)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-white/20 text-gray-400 hover:bg-white/5 hover:text-white"
                  >
                    Personalize
                  </Button>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* AI Suggestions */}
      <Card className="glass-card rounded-xl p-6">
        <h3 className="text-sm text-gray-400 mb-4 flex items-center gap-2">
          <Zap className="h-4 w-4 text-cyan-400/50" />
          AI Content Suggestions
        </h3>
        <div className="space-y-3">
          <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-4">
            <p className="text-sm text-gray-400 leading-relaxed">
              Based on your recent wins in the enterprise segment, consider creating a case study highlighting implementation speed and ROI metrics.
            </p>
          </div>
          <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-4">
            <p className="text-sm text-gray-400 leading-relaxed">
              Your "Initial Outreach - Executive" template has a 32% engagement rate. Try A/B testing a variant with a more direct value proposition.
            </p>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default ContentView;
